import { useId, type ReactElement, type ReactNode } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  IFlipDirection,
  IJobType,
  IMetals,
  IOther,
  IPlastic,
  IWood,
  IXYDatumPosition,
  IZZeroPosition,
} from '@/redux/post/post.d';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 3,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/** The block every flat job is drawn on. Three faces, each a step darker, so
 * the form reads at 56px without a light source or a gradient. */
const BLOCK = (
  <>
    <polygon
      points="60,18 104,40 60,62 16,40"
      fill="currentColor"
      opacity={0.25}
    />
    <polygon
      points="104,40 104,66 60,88 60,62"
      fill="currentColor"
      opacity={0.45}
    />
    <polygon
      points="16,40 16,66 60,88 60,62"
      fill="currentColor"
      opacity={0.6}
    />
  </>
);

const CYLINDER = (
  <>
    <rect
      x="26"
      y="33"
      width="68"
      height="38"
      fill="currentColor"
      opacity={0.45}
    />
    <ellipse
      cx="26"
      cy="52"
      rx="13"
      ry="19"
      fill="currentColor"
      opacity={0.6}
    />
    <ellipse
      cx="94"
      cy="52"
      rx="13"
      ry="19"
      fill="currentColor"
      opacity={0.25}
    />
  </>
);

/** No `<title>`: these sit inside a RadioCard whose accessible name the E2E
 * suite matches on, and a title would join it. */
function Art({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 120 100" aria-hidden className="w-16 h-14 flex-none">
      {children}
    </svg>
  );
}

const JOB_TYPE_ART: Record<IJobType, ReactNode> = {
  [IJobType.SINGLE_SIDED]: (
    <>
      {BLOCK}
      <path d="M60 2v9m-5-3 5 5 5-5" {...STROKE} />
    </>
  ),
  [IJobType.DOUBLE_SIDED]: (
    <>
      {BLOCK}
      <path d="M60 2v9m-5-3 5 5 5-5" {...STROKE} />
      <path d="M60 99v-9m-5 3 5-5 5 5" {...STROKE} />
    </>
  ),
  [IJobType.ROTARY]: (
    <>
      {CYLINDER}
      <path d="M94 22a30 30 0 0 1 0 60" {...STROKE} opacity={0.7} />
    </>
  ),
  [IJobType.TILE]: (
    <>
      {BLOCK}
      <path
        d="M38 29 82 51M38 51 82 29"
        stroke="currentColor"
        strokeWidth={2}
        opacity={0.55}
      />
    </>
  ),
};

const Z_ZERO_ART: Record<IZZeroPosition, ReactNode> = {
  [IZZeroPosition.MATERIAL_SURFACE]: (
    <>
      {BLOCK}
      <circle cx="60" cy="62" r="7" className="fill-destructive" />
    </>
  ),
  [IZZeroPosition.MACHINE_BED]: (
    <>
      <polygon
        points="60,37 117,66 60,95 3,66"
        fill="currentColor"
        opacity={0.15}
      />
      {BLOCK}
      <circle cx="60" cy="88" r="7" className="fill-destructive" />
    </>
  ),
  [IZZeroPosition.CYLINDER_SURFACE]: (
    <>
      {CYLINDER}
      <circle cx="60" cy="33" r="7" className="fill-destructive" />
    </>
  ),
  [IZZeroPosition.CYLINDER_AXIS]: (
    <>
      {CYLINDER}
      <path
        d="M8 52h104"
        stroke="currentColor"
        strokeWidth={2}
        strokeDasharray="6 4"
        opacity={0.7}
      />
      <circle cx="26" cy="52" r="7" className="fill-destructive" />
    </>
  ),
};

export function JobTypeArt({ type }: { type: IJobType }) {
  return <Art>{JOB_TYPE_ART[type]}</Art>;
}

export function ZZeroArt({ value }: { value: IZZeroPosition }) {
  return <Art>{Z_ZERO_ART[value]}</Art>;
}

/** Roughly what each stock material looks like: the base colour of a cut face,
 * and the finish drawn over it. */
const MATERIAL_LOOK: Record<string, { base: string; finish: Finish }> = {
  [IWood.WOOD_HARD]: { base: '#8a5a2b', finish: 'grain' },
  [IWood.WOOD_SOFT]: { base: '#c8a06a', finish: 'grain' },
  [IWood.PLYWOOD]: { base: '#d2a97a', finish: 'plies' },
  [IWood.MDF]: { base: '#a9784f', finish: 'matte' },
  [IMetals.ALUMINUM]: { base: '#b6bcc2', finish: 'brushed' },
  [IMetals.BRASS]: { base: '#c9a227', finish: 'brushed' },
  [IMetals.COPPER]: { base: '#b87333', finish: 'brushed' },
  [IPlastic.PLASTIC]: { base: '#7f8a96', finish: 'gloss' },
  [IPlastic.ACRYLIC]: { base: '#9fd4e6', finish: 'gloss' },
  [IPlastic.HDPE]: { base: '#d9dee2', finish: 'matte' },
  [IOther.GLASS]: { base: '#bfe0ea', finish: 'gloss' },
  [IOther.FOAM]: { base: '#e6e0d2', finish: 'matte' },
  [IOther.EVA]: { base: '#6f7b8a', finish: 'matte' },
  [IOther.LEATHER]: { base: '#7a4b32', finish: 'matte' },
};

type Finish = 'grain' | 'plies' | 'brushed' | 'gloss' | 'matte';

/** Same hue, darker — the two side faces of the block are the top face turned
 * away from the light. */
function shade(hex: string, factor: number): string {
  const ch = [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16))
    .map((c) => Math.max(0, Math.min(255, Math.round(c * factor))));
  return `rgb(${ch[0]} ${ch[1]} ${ch[2]})`;
}

const num = (v: string, fallback: number): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

type Pt = [number, number];

const GREY_LOOK = { base: '#9aa0a6', finish: 'matte' as Finish };

/** Nothing typed yet — every size field blank, so each falls to its own default
 * in `stockGeometry` and prints as nothing. */
const BLANK_STOCK = { x: '', y: '', z: '', unit: '' };
const BLANK_ROTARY = { length: '', diameter: '', unit: '' };

/** How this material is drawn, or a neutral grey for one we have no look for. */
const materialLook = (material?: string) =>
  MATERIAL_LOOK[material ?? ''] ?? GREY_LOOK;

/** The inch mark, or nothing when the G-code was written in mm. */
const unitMark = (unit: string) => (unit === 'in' ? '"' : '');

/** A size with its unit mark, ready to print — blank while the field is empty. */
const sizeValue = (v: string, unit: string) =>
  v ? `${v}${unitMark(unit)}` : '';

/** Every size field printed, keyed as it was passed in. */
function sizeLabels<K extends string>(
  sizes: Record<K, string>,
  unit: string
): Record<K, string> {
  const printed = Object.entries(sizes).map(([k, v]) => [
    k,
    sizeValue(v as string, unit),
  ]);
  return Object.fromEntries(printed) as Record<K, string>;
}

const add = (p: Pt, q: Pt): Pt => [p[0] + q[0], p[1] + q[1]];
const mul = (p: Pt, k: number): Pt => [p[0] * k, p[1] * k];
const poly = (...p: Pt[]) => p.map(([x, y]) => `${x},${y}`).join(' ');

/** The stock block's geometry, in the isometric basis the diagram is drawn in. */
interface StockGeometry {
  top: Pt;
  left: Pt;
  right: Pt;
  front: Pt;
  vX: Pt;
  vY: Pt;
  dz: number;
  faces: { top: string; left: string; right: string };
  labelY: number;
  box: string;
}

/**
 * X down-left, Y down-right, Z straight down — so each arrow runs along the edge
 * a user measures, and the viewBox is computed from the block itself so a long
 * board and a square one both fill the card.
 */
export function stockGeometry(size: {
  x: string;
  y: string;
  z: string;
}): StockGeometry {
  const w = num(size.x, 12);
  const h = num(size.y, 9);
  const t = num(size.z, 1.5);
  const span = Math.max(w, h);

  // Thickness is proportional only within a range that stays readable: drawn to
  // true scale, a 0.75" sheet on a 24" board is a couple of pixels.
  const fz = Math.max(0.14, Math.min(0.5, t / span));

  const vX: Pt = [-190 * (w / span), 92 * (w / span)];
  const vY: Pt = [190 * (h / span), 92 * (h / span)];
  const dz = 150 * fz;

  const top: Pt = [0, 0];
  const left = add(top, vX);
  const right = add(top, vY);
  const front = add(left, vY);
  const drop = (p: Pt): Pt => [p[0], p[1] + dz];

  // Room for the labels: two lines under each bottom edge, and the Z callout to
  // the right of its arrow.
  const labelY = front[1] + dz + 46;
  // The contact shadow is wider than the block itself, so the box has to cover
  // it too — sized off the block alone, its soft edge was cut square.
  const shadowRx = Math.abs(vX[0]) + Math.abs(vY[0]) * 0.5;
  const minX = Math.min(left[0] - 70, front[0] - shadowRx);
  const maxX = Math.max(right[0] + 150, front[0] + shadowRx);

  return {
    top,
    left,
    right,
    front,
    vX,
    vY,
    dz,
    faces: {
      top: poly(top, left, front, right),
      left: poly(left, front, drop(front), drop(left)),
      right: poly(right, front, drop(front), drop(right)),
    },
    labelY,
    box: [minX, -30, maxX - minX, labelY + 60].join(' '),
  };
}

function StockDefs({ uid }: { uid: string }) {
  return (
    <defs>
      <marker
        id={`${uid}-arrow`}
        viewBox="0 0 8 8"
        refX="7"
        refY="4"
        markerWidth="3.4"
        markerHeight="3.4"
        orient="auto"
      >
        <path d="M0 0 8 4 0 8z" className="fill-primary" />
      </marker>
      <linearGradient id={`${uid}-sheen`} x1="0" y1="0" x2="0.9" y2="1">
        <stop offset="0%" stopColor="#fff" stopOpacity={0.34} />
        <stop offset="50%" stopColor="#fff" stopOpacity={0.05} />
        <stop offset="100%" stopColor="#000" stopOpacity={0.16} />
      </linearGradient>
      <radialGradient id={`${uid}-shadow`}>
        <stop offset="0%" stopColor="#000" stopOpacity={0.3} />
        <stop offset="100%" stopColor="#000" stopOpacity={0} />
      </radialGradient>
    </defs>
  );
}

/** Lines that span the top face edge to edge, stepped along the other axis —
 * the grain of a board runs the length of it, and a brushed finish likewise. */
function streaks(g: StockGeometry, n: number) {
  return Array.from({ length: n }, (_, i) => {
    const u = (i + 0.5) / n;
    const a = add(g.top, mul(g.vY, u));
    return { a, b: add(a, g.vX), u };
  });
}

const grainStroke = (base: string, u: number) =>
  shade(base, u % 0.34 < 0.17 ? 0.7 : 1.18);
const grainWidth = (u: number) => (u % 0.25 < 0.12 ? 3 : 1.4);
const brushedStroke = (u: number) => (u % 0.16 < 0.08 ? '#fff' : '#000');

function GrainFinish({ g, base }: { g: StockGeometry; base: string }) {
  return (
    <g strokeLinecap="round" fill="none">
      {streaks(g, 11).map(({ a, b, u }) => (
        <path
          key={u}
          d={`M${a[0]},${a[1]} Q${(a[0] + b[0]) / 2 + 14},${(a[1] + b[1]) / 2 - 8} ${b[0]},${b[1]}`}
          stroke={grainStroke(base, u)}
          strokeWidth={grainWidth(u)}
          opacity={0.55}
        />
      ))}
    </g>
  );
}

function BrushedFinish({ g }: { g: StockGeometry }) {
  return (
    <g>
      {streaks(g, 26).map(({ a, b, u }) => (
        <line
          key={u}
          x1={a[0]}
          y1={a[1]}
          x2={b[0]}
          y2={b[1]}
          stroke={brushedStroke(u)}
          strokeWidth={1.2}
          opacity={0.12}
        />
      ))}
    </g>
  );
}

function PliesFinish({ g, base }: { g: StockGeometry; base: string }) {
  return (
    <>
      {[0.28, 0.56, 0.82].map((f) => (
        <g key={f} strokeWidth={1.6} opacity={0.7}>
          <line
            x1={g.left[0]}
            y1={g.left[1] + g.dz * f}
            x2={g.front[0]}
            y2={g.front[1] + g.dz * f}
            stroke={shade(base, 0.58)}
          />
          <line
            x1={g.front[0]}
            y1={g.front[1] + g.dz * f}
            x2={g.right[0]}
            y2={g.right[1] + g.dz * f}
            stroke={shade(base, 0.45)}
          />
        </g>
      ))}
    </>
  );
}

/** The texture over the bare faces. One component per finish, picked by name. */
const FINISH_ART: Record<
  Finish,
  ((props: { g: StockGeometry; base: string }) => ReactElement) | null
> = {
  grain: GrainFinish,
  brushed: ({ g }) => <BrushedFinish g={g} />,
  plies: PliesFinish,
  gloss: null,
  matte: null,
};

function StockFinish({
  g,
  look,
}: {
  g: StockGeometry;
  look: { base: string; finish: Finish };
}) {
  const Overlay = FINISH_ART[look.finish];
  return Overlay ? <Overlay g={g} base={look.base} /> : null;
}

/** Each arrow runs parallel to its own edge, offset clear of the block. */
function StockArrows({ g, uid }: { g: StockGeometry; uid: string }) {
  const { front, left, right, dz } = g;
  return (
    <g
      className="stroke-primary"
      strokeWidth={1.6}
      markerEnd={`url(#${uid}-arrow)`}
    >
      <line
        x1={front[0] - 10}
        y1={front[1] + dz + 22}
        x2={left[0] - 18}
        y2={left[1] + dz + 14}
      />
      <line
        x1={front[0] + 10}
        y1={front[1] + dz + 22}
        x2={right[0] + 18}
        y2={right[1] + dz + 14}
      />
      <line
        x1={right[0] + 28}
        y1={right[1] + dz}
        x2={right[0] + 28}
        y2={right[1] + 2}
      />
    </g>
  );
}

/** Where each bottom label sits. front = right + vX and front = left + vY, so
 * the X edge runs to `right` and the Y edge to `left` — not the other way. */
export function labelMids(g: StockGeometry) {
  return {
    xMid: (g.front[0] + g.right[0]) / 2 + 14,
    yMid: (g.front[0] + g.left[0]) / 2 - 14,
  };
}

/** Both bottom labels share one baseline, below everything else — the two edges
 * end at different heights, and following them would stagger the two blocks. */
function StockLabels({
  g,
  size,
}: {
  g: StockGeometry;
  size: { x: string; y: string; z: string };
}) {
  const { right, dz, labelY } = g;
  const { xMid, yMid } = labelMids(g);
  return (
    <g className="font-tertiary" fontWeight="700">
      <g className="fill-primary" fontSize="12" textAnchor="middle">
        <text x={xMid} y={labelY}>
          X (Width)
        </text>
        <text x={yMid} y={labelY}>
          Y (Height)
        </text>
      </g>
      <g className="fill-foreground" fontSize="13" textAnchor="middle">
        <text x={xMid} y={labelY + 18}>
          {size.x}
        </text>
        <text x={yMid} y={labelY + 18}>
          {size.y}
        </text>
      </g>
      <text
        className="fill-primary"
        fontSize="12"
        x={right[0] + 40}
        y={right[1] + dz / 2 - 4}
      >
        Z (Thickness)
      </text>
      <text
        className="fill-foreground"
        fontSize="13"
        x={right[0] + 40}
        y={right[1] + dz / 2 + 15}
      >
        {size.z}
      </text>
    </g>
  );
}

/**
 * The stock as it would actually sit on the bed: proportioned from the three
 * size fields, coloured like the chosen material, with each field's value
 * called out on its own edge.
 */
export function StockDiagram({
  dims = BLANK_STOCK,
  material,
}: {
  dims?: { x: string; y: string; z: string; unit: string };
  material?: string;
}) {
  const uid = useId();
  const look = materialLook(material);
  const g = stockGeometry(dims);

  return (
    <svg
      viewBox={g.box}
      aria-hidden
      className="mx-auto block w-full max-w-[560px]"
    >
      <StockDefs uid={uid} />

      <ellipse
        cx={g.front[0]}
        cy={g.front[1] + g.dz + 6}
        rx={Math.abs(g.vX[0]) + Math.abs(g.vY[0]) * 0.5}
        ry={26}
        fill={`url(#${uid}-shadow)`}
      />

      <polygon points={g.faces.top} fill={look.base} />
      <polygon points={g.faces.left} fill={shade(look.base, 0.74)} />
      <polygon points={g.faces.right} fill={shade(look.base, 0.56)} />

      <StockFinish g={g} look={look} />

      <polygon points={g.faces.top} fill={`url(#${uid}-sheen)`} />

      <StockArrows g={g} uid={uid} />
      <StockLabels
        g={g}
        size={sizeLabels({ x: dims.x, y: dims.y, z: dims.z }, dims.unit)}
      />
    </svg>
  );
}

/** Where each datum sits on the board, seen from above — as a share of the
 * OUTER box, which is inset by half a hit target on every side. */
const DATUM_POS: Record<IXYDatumPosition, { left: string; top: string }> = {
  [IXYDatumPosition.UPPER_LEFT]: { left: '9.8%', top: '12.5%' },
  [IXYDatumPosition.UPPER_RIGHT]: { left: '90.2%', top: '12.5%' },
  [IXYDatumPosition.LOWER_LEFT]: { left: '9.8%', top: '87.5%' },
  [IXYDatumPosition.LOWER_RIGHT]: { left: '90.2%', top: '87.5%' },
  [IXYDatumPosition.CENTER]: { left: '50%', top: '50%' },
};

/** The turned blank's proportions, and the viewBox that frames them. */
interface RotaryGeometry {
  L: number;
  R: number;
  capRx: number;
  labelY: number;
  box: string;
}

function rotaryGeometry(size: { length: string; diameter: string }) {
  const len = num(size.length, 12);
  const dia = num(size.diameter, 4);
  const span = Math.max(len, dia);
  const L = 340 * (len / span);
  const R = (170 * (dia / span)) / 2;
  const capRx = Math.max(10, R * 0.34);
  const labelY = R + 54;
  const boxTop = -R - 40;

  return {
    L,
    R,
    capRx,
    labelY,
    box: [-capRx - 40, boxTop, L + capRx * 2 + 190, labelY + 34 - boxTop].join(
      ' '
    ),
  };
}

function RotaryDefs({
  uid,
  g,
  base,
}: {
  uid: string;
  g: RotaryGeometry;
  base: string;
}) {
  return (
    <defs>
      <marker
        id={`${uid}-arrow`}
        viewBox="0 0 8 8"
        refX="7"
        refY="4"
        markerWidth="3.4"
        markerHeight="3.4"
        orient="auto"
      >
        <path d="M0 0 8 4 0 8z" className="fill-primary" />
      </marker>
      <linearGradient id={`${uid}-round`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={shade(base, 0.62)} />
        <stop offset="32%" stopColor={shade(base, 1.12)} />
        <stop offset="70%" stopColor={base} />
        <stop offset="100%" stopColor={shade(base, 0.5)} />
      </linearGradient>
      <radialGradient id={`${uid}-shadow`}>
        <stop offset="0%" stopColor="#000" stopOpacity={0.3} />
        <stop offset="100%" stopColor="#000" stopOpacity={0} />
      </radialGradient>
      <clipPath id={`${uid}-body`}>
        <rect x={0} y={-g.R} width={g.L} height={g.R * 2} />
      </clipPath>
    </defs>
  );
}

/** Turned stock shows its grain (or its brushing) running the length. */
function RotaryGrain({ uid, g }: { uid: string; g: RotaryGeometry }) {
  const ys = Array.from(
    { length: 9 },
    (_, i) => -g.R + ((i + 0.5) * 2 * g.R) / 9
  );
  return (
    <g clipPath={`url(#${uid}-body)`} opacity={0.3}>
      {ys.map((y) => (
        <line
          key={y}
          x1={0}
          y1={y}
          x2={g.L}
          y2={y}
          stroke={y < 0 ? '#fff' : '#000'}
          strokeWidth={1.6}
        />
      ))}
    </g>
  );
}

function RotaryArrows({ uid, g }: { uid: string; g: RotaryGeometry }) {
  const { L, R, capRx } = g;
  return (
    <g
      className="stroke-primary"
      strokeWidth={1.6}
      markerEnd={`url(#${uid}-arrow)`}
    >
      <line x1={L / 2 - 10} y1={R + 34} x2={0} y2={R + 34} />
      <line x1={L / 2 + 10} y1={R + 34} x2={L} y2={R + 34} />
      <line x1={L + capRx + 34} y1={0} x2={L + capRx + 34} y2={-R} />
      <line x1={L + capRx + 34} y1={0} x2={L + capRx + 34} y2={R} />
    </g>
  );
}

function RotaryLabels({
  g,
  size,
}: {
  g: RotaryGeometry;
  size: { length: string; diameter: string };
}) {
  const { L, capRx, labelY } = g;
  return (
    <g className="font-tertiary" fontWeight="700">
      <text
        className="fill-primary"
        fontSize="12"
        textAnchor="middle"
        x={L / 2}
        y={labelY}
      >
        Length
      </text>
      <text
        className="fill-foreground"
        fontSize="13"
        textAnchor="middle"
        x={L / 2}
        y={labelY + 18}
      >
        {size.length}
      </text>
      <text className="fill-primary" fontSize="12" x={L + capRx + 46} y={-4}>
        Diameter
      </text>
      <text className="fill-foreground" fontSize="13" x={L + capRx + 46} y={14}>
        {size.diameter}
      </text>
    </g>
  );
}

/** The rotary blank: a cylinder in the same material, with the two sizes the
 * G-code can't supply called out on its length and its face. */
export function RotaryStockDiagram({
  dims = BLANK_ROTARY,
  material,
}: {
  dims?: { length: string; diameter: string; unit: string };
  material?: string;
}) {
  const uid = useId();
  const look = materialLook(material);
  const g = rotaryGeometry(dims);
  const { L, R, capRx } = g;

  return (
    <svg
      viewBox={g.box}
      aria-hidden
      className="mx-auto block w-full max-w-[560px]"
    >
      <RotaryDefs uid={uid} g={g} base={look.base} />

      <ellipse
        cx={L / 2}
        cy={R + 14}
        rx={L * 0.55}
        ry={18}
        fill={`url(#${uid}-shadow)`}
      />

      <ellipse cx={L} cy={0} rx={capRx} ry={R} fill={shade(look.base, 0.8)} />
      <rect x={0} y={-R} width={L} height={R * 2} fill={`url(#${uid}-round)`} />

      {look.finish !== 'matte' && <RotaryGrain uid={uid} g={g} />}

      {/* The near face: end grain, so it is drawn flat rather than shaded. */}
      <ellipse cx={0} cy={0} rx={capRx} ry={R} fill={shade(look.base, 0.66)} />
      <ellipse
        cx={0}
        cy={0}
        rx={capRx * 0.55}
        ry={R * 0.55}
        fill={shade(look.base, 0.58)}
      />

      <RotaryArrows uid={uid} g={g} />
      <RotaryLabels
        g={g}
        size={sizeLabels(
          { length: dims.length, diameter: dims.diameter },
          dims.unit
        )}
      />
    </svg>
  );
}

/**
 * The board seen from above, with a dot at each place the machine can be
 * zeroed — the control VCarve draws, because "Lower Left" only means something
 * against a drawn board. Five tiles said the same thing down half a screen.
 *
 * A real `RadioGroup` rather than clickable SVG nodes: a `<circle>` needs its
 * own focus and key handling, and a corner dot is far too small a hit area on
 * its own — the `<label>` around each item is what makes it 44px.
 */
export function XYDatumBoard({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: IXYDatumPosition) => void;
}) {
  const uid = useId();
  return (
    // Inset by half a hit target, so a corner dot is comfortable to click
    // without any of it hanging outside the control.
    <RadioGroup
      value={value}
      onValueChange={(v) => onChange(v as IXYDatumPosition)}
      className="relative block h-[132px] w-[176px]"
    >
      <div className="absolute inset-[22px] rounded-sm border-2 border-border-strong bg-muted/40" />
      {Object.entries(DATUM_POS).map(([datum, pos]) => (
        <label
          key={datum}
          htmlFor={`${uid}-${datum}`}
          style={pos}
          className="absolute flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-colors duration-fast hover:bg-foreground/[0.06]"
        >
          <span className="sr-only">{datum}</span>
          <RadioGroupItem
            id={`${uid}-${datum}`}
            value={datum}
            aria-label={datum}
            className="h-4 w-4 border-2 border-muted-foreground/60 bg-background data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
          />
        </label>
      ))}
    </RadioGroup>
  );
}

/** Which way the board is turned over between the two sides — the flat plank
 * VCarve draws, with the arrow arcing about the edge it spins on. */
export function FlipArt({ direction }: { direction: IFlipDirection }) {
  const sideways = direction === IFlipDirection.LEFT_TO_RIGHT;
  const uid = useId();
  return (
    <Art>
      <marker
        id={`${uid}-flip`}
        viewBox="0 0 8 8"
        refX="6"
        refY="4"
        markerWidth="3.5"
        markerHeight="3.5"
        orient="auto"
      >
        <path d="M0 0 8 4 0 8z" className="fill-primary" />
      </marker>

      {/* The plank turns with the flip — long edge across for left to right,
          receding for front to back — so the two icons can't read alike. */}
      {sideways ? (
        <>
          <path
            d="M22 66 94 66 94 75 22 75Z"
            fill="currentColor"
            opacity={0.6}
          />
          <path
            d="M94 66 108 56 108 65 94 75Z"
            fill="currentColor"
            opacity={0.45}
          />
          <path
            d="M22 66 94 66 108 56 36 56Z"
            fill="currentColor"
            opacity={0.25}
          />
        </>
      ) : (
        <>
          <path
            d="M22 76 54 76 54 85 22 85Z"
            fill="currentColor"
            opacity={0.6}
          />
          <path
            d="M54 76 92 50 92 59 54 85Z"
            fill="currentColor"
            opacity={0.45}
          />
          <path
            d="M22 76 54 76 92 50 60 50Z"
            fill="currentColor"
            opacity={0.25}
          />
        </>
      )}

      {/* Foreshortened, and sitting low: at 64px a taller plank leaves the arc
          nowhere to go and the two shapes merge. */}
      <path
        d={sideways ? 'M20 62C26 16 96 10 104 50' : 'M26 72C10 20 78 4 88 42'}
        fill="none"
        className="stroke-primary"
        strokeWidth={4}
        strokeLinecap="round"
        markerEnd={`url(#${uid}-flip)`}
      />
    </Art>
  );
}
